import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Button } from '@carbon/react';

import App from './App';
import { useAuthStore } from './store/authStore';
import { initializePerformanceOptimizations, reportWebVitals } from './utils/performance';
import I18nProvider from './components/i18n/I18nProvider';
import './i18n/config';

import '@carbon/styles/css/styles.css'; // Core Carbon Design System styles
import './styles/global.css';
import './styles/dashboard.css';
import './styles/webkit-fixes.css'; // Safari/WebKit compatibility fixes
import './styles/carbon.css'; // Carbon Design System styles
import './styles/carbon-spacing.css'; // Carbon Design System spacing compliance
import './styles/carbon-spacing-fix.css'; // Carbon 8px grid spacing enforcement
import './styles/carbon-compliance-fixes.css'; // Carbon Design System compliance fixes for conversion optimization
import './styles/carbon-header-fixes.css'; // Carbon header and hero section compliance fixes
import './styles/collaboration-accessibility.scss'; // Accessibility fixes for collaboration components
import './styles/navigation-z-index-fixes.css'; // Navigation z-index management and overlap fixes
import './styles/mobile-responsive-fixes.css'; // Epic75 mobile responsive and WCAG 1.4.10 compliance fixes
import './styles/carbon-typography-fix.css'; // Epic75 Carbon typography compliance - H1 minimum 20px

// Global error handlers for unhandled promise rejections and errors
window.addEventListener('unhandledrejection', event => {
  console.error('🚨 Unhandled Promise Rejection:', event.reason);

  // Report to error tracking service if available
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'exception', {
      description: `Unhandled Promise Rejection: ${event.reason}`,
      fatal: false,
    });
  }

  // Prevent the default behavior (which would log to console)
  event.preventDefault();

  // Show user-friendly error message for critical auth failures
  if (event.reason?.message?.includes('Authentication') || event.reason?.message?.includes('token')) {
    console.warn('Authentication error detected, clearing tokens');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
  }
});

window.addEventListener('error', event => {
  console.error('🚨 Global Error:', event.error, 'in', event.filename, 'at line', event.lineno);

  // Report to error tracking service if available
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'exception', {
      description: `Global Error: ${event.error?.message || event.message}`,
      fatal: false,
    });
  }
});

// Carbon theme is now handled by the Theme component - no manual CSS property setting needed

// Error boundary to catch and display React errors
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('React Error Boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'Inter, system-ui' }}>
          <h2 style={{ color: 'var(--cds-support-error)', marginBottom: '16px' }}>Application Error</h2>
          <p style={{ color: 'var(--cds-text-secondary)', marginBottom: '24px' }}>
            An error occurred while loading the application.
          </p>
          <details style={{ textAlign: 'left', maxWidth: '600px', margin: '0 auto' }}>
            <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>Error Details</summary>
            <pre style={{ background: '#f5f5f5', padding: '16px', overflow: 'auto' }}>
              {this.state.error?.toString()}
            </pre>
          </details>
          <Button
            onClick={() => window.location.reload()}
            kind="primary"
            size="lg"
            style={{ marginTop: 'var(--cds-spacing-06)' }}
          >
            Reload Page
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

function Root() {
  const { user } = useAuthStore();

  // Initialize app and notify that React has mounted
  React.useEffect(() => {
    // Call the global React mounted handler if it exists
    if (typeof window.__REACT_MOUNTED__ === 'function') {
      window.__REACT_MOUNTED__();
    } else {
      // Fallback: directly remove loading screen if handler doesn't exist
      console.warn('__REACT_MOUNTED__ handler not found, removing loading screen directly');
      const loadingElement = document.getElementById('loading');
      if (loadingElement) {
        loadingElement.style.display = 'none';
      }
    }
  }, []);

  return (
    <ErrorBoundary>
      <I18nProvider detectLocation={true}>
        <HelmetProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </HelmetProvider>
      </I18nProvider>
    </ErrorBoundary>
  );
}

// Initialize performance optimizations
initializePerformanceOptimizations();

// Report web vitals in production
if (import.meta.env.PROD) {
}

// Simple app initialization with error handling

const rootElement = document.getElementById('root');
if (rootElement) {
  try {
    const root = ReactDOM.createRoot(rootElement);

    // BUGFIX: Disable StrictMode in staging/production to prevent double-mounting
    // StrictMode intentionally mounts components twice to detect side effects
    // Enable only for local development (localhost), disable for staging/production
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    console.log('[Sigma08] StrictMode:', isLocalDev ? 'ENABLED (localhost)' : 'DISABLED (staging/prod)');

    root.render(
      isLocalDev ? (
        <React.StrictMode>
          <Root />
        </React.StrictMode>
      ) : (
        <Root />
      )
    );
  } catch (error) {
    console.error('❌ Failed to initialize React app:', error);
    // The vanilla JS fallback in index.html will handle this
  }
} else {
  console.error('❌ Root element not found, cannot mount React app');
}
// Cache bust Wed Oct  8 17:10:42 BST 2025
